import dayjs from 'dayjs'
import type { AdvisoryAiReportRawData } from '@/features/advisory-ai/types'

// One subsection within a report type.
export type SourceSubsection = {
  /** Generic label shown in the UI (e.g. "Summary", "Monthly Detailed"). */
  label: string
  /** Key to look up in the report JSON — combined data for consolidated, the org's own for single-org. */
  dataKey: string
  /**
   * When true, this subsection renders a column per company and is shown for consolidated
   * analyses only (hidden in single-organisation analyses). It reuses the same `dataKey` as
   * its non-perOrg counterpart — "Summary Per Org" breaks down the same data as "Summary".
   */
  perOrg?: boolean
}

// Report type -> ordered subsections. The display order follows the requested sequence:
// Summary, Detailed, Monthly Summary, Monthly Detailed, Summary Per Org, Detailed Per Org, Top 20.
// Report types only list the subsections they actually have (e.g. AP/AR have no Detailed).
// Shared by the single-org (Report ▸ Subsection) and consolidated
// (Report ▸ Subsection, with per-company columns on "… Per Org") Source Reports views.
export const REPORT_SUBSECTIONS: Array<{ label: string; subsections: SourceSubsection[] }> = [
  {
    label: 'Accounts Payable',
    subsections: [
      { label: 'Summary', dataKey: 'Creditors Summary' },
      { label: 'Monthly Summary', dataKey: 'Creditors Monthly' },
      { label: 'Summary Per Org', dataKey: 'Creditors Summary', perOrg: true },
      { label: 'Top 20', dataKey: 'Top 20 Suppliers on Creditors' },
    ],
  },
  {
    label: 'Accounts Receivable',
    subsections: [
      { label: 'Summary', dataKey: 'Debtors Summary' },
      { label: 'Monthly Summary', dataKey: 'Debtors Monthly' },
      { label: 'Summary Per Org', dataKey: 'Debtors Summary', perOrg: true },
      { label: 'Top 20', dataKey: 'Top 20 Customers on Debtors' },
    ],
  },
  {
    label: 'Balance Sheet',
    subsections: [
      { label: 'Summary', dataKey: 'Balance Sheet Summarised' },
      { label: 'Detailed', dataKey: 'Balance Sheet Detailed' },
      { label: 'Monthly Summary', dataKey: 'Balance Sheet Monthly Summarised' },
      { label: 'Monthly Detailed', dataKey: 'Balance Sheet Monthly Detailed' },
      { label: 'Summary Per Org', dataKey: 'Balance Sheet Summarised', perOrg: true },
      { label: 'Detailed Per Org', dataKey: 'Balance Sheet Detailed', perOrg: true },
    ],
  },
  {
    label: 'Cashflow',
    subsections: [
      { label: 'Summary', dataKey: 'Cashflow Summary' },
      { label: 'Detailed', dataKey: 'Cashflow Detailed' },
      { label: 'Monthly Summary', dataKey: 'Cashflow Monthly Summarised' },
      { label: 'Monthly Detailed', dataKey: 'Cashflow Monthly Detailed' },
      { label: 'Summary Per Org', dataKey: 'Cashflow Summary', perOrg: true },
      { label: 'Detailed Per Org', dataKey: 'Cashflow Detailed', perOrg: true },
    ],
  },
  {
    label: 'Profitability',
    subsections: [
      { label: 'Summary', dataKey: 'P&L Summarised' },
      { label: 'Detailed', dataKey: 'P&L Detailed' },
      { label: 'Monthly Summary', dataKey: 'P&L Monthly Summarised' },
      { label: 'Monthly Detailed', dataKey: 'P&L Monthly Detailed' },
      { label: 'Summary Per Org', dataKey: 'P&L Summarised', perOrg: true },
      { label: 'Detailed Per Org', dataKey: 'P&L Detailed', perOrg: true },
    ],
  },
]

// The "Monthly" source reports pivot 12 month-ends into columns M1..M12, where
// M1 = date_trunc('month', EndDate) - 11 months and M12 = EndDate's month
// (see ai-function .../Balance Sheet Monthly Detailed.sql). Turn those into month labels.
const M_COLUMN = /^M(\d{1,2})$/

export function monthColumnTitle(key: string, endDate?: string | null): string {
  const match = M_COLUMN.exec(key)
  if (!match || !endDate) return key
  const monthIndex = Number(match[1])
  if (monthIndex < 1 || monthIndex > 12) return key
  // Use the calendar date (ignore time/zone) so an end-of-month timestamp doesn't roll over.
  const base = dayjs(String(endDate).slice(0, 10))
  if (!base.isValid()) return key
  return base.startOf('month').subtract(12 - monthIndex, 'month').format('MMM YYYY')
}

/** Turn a raw data key into a human-readable header (e.g. "EndingBalancePreviousYear" -> "Ending Balance Previous Year"). */
export function humanizeHeader(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // camelCase boundary: ...sP -> ...s P
    .replace(/([A-Za-z])(\d)/g, '$1 $2') // letter→digit: Aging1 -> Aging 1
    .replace(/(\d)([A-Za-z])/g, '$1 $2') // digit→letter: 1Month -> 1 Month
    .replace(/([A-Za-z])%/g, '$1 %') // Variance% -> Variance %
    .replace(/\s+/g, ' ')
    .trim()
}

/** Resolve a column header: M1..M12 become month labels, everything else is humanized. */
export function columnTitle(key: string, endDate?: string | null): string {
  if (/^M\d{1,2}$/.test(key)) return monthColumnTitle(key, endDate)
  return humanizeHeader(key)
}

/** Columns whose header carries a percentage (formatted with % instead of $). */
export function isPercentColumn(key: string): boolean {
  return key.includes('%') || /variance/i.test(key)
}

const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '')

/** The key in a subsection's rows that holds the month dimension (e.g. "Month"), or null. */
export function findMonthKey(rows: Array<Record<string, unknown>>): string | null {
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (normalize(key) === 'month') return key
    }
  }
  return null
}

// Canonical chart-of-accounts ("accounts master") ordering. The Profitability source
// reports come back in whatever order the query emits; we reorder their rows by the
// "Account Type" column to follow this sequence. Values not listed sort to the end,
// keeping their original relative order. Matching is case-insensitive and trimmed.
export const ACCOUNT_TYPE_ORDER: string[] = [
  'BANK', 'CURRENT', 'REVENUE', 'SALES', 'DIRECTCOSTS', 'OTHERINCOME', 'EXPENSE', 'OVERHEADS',
  'SUPERANNUATIONEXPENSE', 'WAGESEXPENSE', 'NONOPERATINGINCOME', 'NONOPERATINGEXPENSES',
  'FINANCECOSTS', 'INCOMETAX', 'DEPRECIATIONAMORTISATION', 'DEPRECIATN', 'RELATEDPARTYEXPENSES',
  'ASS.CUR', 'ASS.CUR.BAN', 'ASS.CUR.CAS', 'ASS.CUR.DIR', 'ASS.CUR.INY', 'ASS.CUR.LOA',
  'ASS.CUR.PRE', 'ASS.CUR.REC', 'ASS.CUR.REL', 'ASS.CUR.TER', 'ASS.CUR.UDA', 'ASS.NCA',
  'ASS.NCA.DIR', 'ASS.NCA.FIX', 'ASS.NCA.INT', 'ASS.NCA.INV', 'ASS.NCA.LOA', 'ASS.NCA.REA',
  'ASS.NCA.REL', 'ASS.NCA.UDA', 'EQU.ADV', 'EQU.ADV.', 'EQU.DRA', 'EQU.DRA.', 'EQU.GIF',
  'EQU.OWN', 'EQU.PFT', 'EQU.PFT.', 'EQU.PRE', 'EQU.RES', 'EQU.RES.FOR', 'EQU.RES.LRR',
  'EQU.RES.REV', 'EQU.RET', 'EQU.RET.CUR', 'EQU.RET.DIS', 'EQU.RET.DIV', 'EQU.SET', 'EQU.SHA',
  'EQU.SHA.ORD', 'EQU.SHA.PRE', 'EQU.UDE', 'EQU.UDE.', 'EXP.ADM', 'EXP.ADV', 'EXP.AMO',
  'EXP.AUD', 'EXP.BAD', 'EXP.CFL', 'EXP.CON', 'EXP.COS', 'EXP.COS.CLO', 'EXP.COS.OPE',
  'EXP.COS.PUR', 'EXP.COS.WAG', 'EXP.COS.WPC', 'EXP.COS.WPO', 'EXP.DEP', 'EXP.DIR', 'EXP.DIS',
  'EXP.DIV', 'EXP.EMP', 'EXP.ENT', 'EXP.EXT', 'EXP.FAR', 'EXP.FBT', 'EXP.FOR', 'EXP.GRA',
  'EXP.IMP', 'EXP.INC', 'EXP.INS', 'EXP.INT', 'EXP.INV', 'EXP.LEG', 'EXP.LOS', 'EXP.NON',
  'EXP.OCC', 'EXP.OPR', 'EXP.POW', 'EXP.PRI', 'EXP.PRO', 'EXP.RAT', 'EXP.REN', 'EXP.REP',
  'EXP.TAX', 'EXP.TEL', 'EXP.TRA', 'EXP.UDE', 'EXP.UTI', 'EXP.VEH', 'EXP.WAG', 'LIA.CUR',
  'LIA.CUR.CHM', 'LIA.CUR.DEF', 'LIA.CUR.FIN', 'LIA.CUR.HPA', 'LIA.CUR.INC', 'LIA.CUR.LOA',
  'LIA.CUR.PAY', 'LIA.CUR.PRO', 'LIA.CUR.REL', 'LIA.CUR.TAX', 'LIA.CUR.UDL', 'LIA.NCL',
  'LIA.NCL.ADV', 'LIA.NCL.CHM', 'LIA.NCL.DRA', 'LIA.NCL.FIN', 'LIA.NCL.HPA', 'LIA.NCL.LOA',
  'LIA.NCL.PFT', 'LIA.NCL.REL', 'LIA.NCL.UDL', 'REV', 'REV.COM', 'REV.GOO', 'REV.GRA',
  'REV.INV', 'REV.INV.DIV', 'REV.INV.INT', 'REV.INV.REN', 'REV.NON', 'REV.OTH', 'REV.OTH.CAP',
  'REV.OTH.COM', 'REV.OTH.GAI', 'REV.OTH.INV', 'REV.SAL', 'REV.TRA', 'REV.UDR', 'CURRLIAB',
  'FIXED', 'INVENTORY', 'LIABILITY', 'NONCURRENT', 'TERMLIAB', 'PAYGLIABILITY',
  'SUPERANNUATIONLIABILITY', 'Accounts Receivable_AccountsReceivable', 'Bank_CashAndCashEquivalents',
  'Bank_CashOnHand', 'Bank_Checking', 'Bank_Savings', 'Fixed Asset_AccumulatedDepletion',
  'Fixed Asset_AccumulatedDepreciation', 'Fixed Asset_Buildings', 'Fixed Asset_FurnitureAndFixtures',
  'Fixed Asset_LeaseholdImprovements', 'Fixed Asset_MachineryAndEquipment', 'Fixed Asset_NonCurrentAssets',
  'Fixed Asset_OtherFixedAssets', 'Fixed Asset_Vehicles', 'Other Asset_Goodwill',
  'Other Asset_LongTermInvestments', 'Other Asset_OtherLongTermAssets', 'Other Current Asset_Inventory',
  'Other Current Asset_OtherCurrentAssets', 'Other Current Asset_PrepaidExpenses',
  'Other Current Asset_TradeAndOtherReceivables', 'Other Current Asset_UndepositedFunds',
  'Equity_AccumulatedAdjustment', 'Equity_CommonStock', 'Equity_DividendDisbursed',
  'Equity_OpeningBalanceEquity', 'Equity_OwnersEquity', 'Equity_PaidInCapitalOrSurplus',
  'Equity_RetainedEarnings', 'Cost of Goods Sold_CostOfSales',
  'Cost of Goods Sold_ShippingFreightDeliveryCos', 'Cost of Goods Sold_SuppliesMaterialsCogs',
  'Expense_AdvertisingPromotional', 'Expense_AmortizationExpense', 'Expense_Auto', 'Expense_BadDebts',
  'Expense_BankCharges', 'Expense_CharitableContributions', 'Expense_CommissionsAndFees',
  'Expense_CostOfLabor', 'Expense_DuesSubscriptions', 'Expense_EntertainmentMeals',
  'Expense_EquipmentRental', 'Expense_ExternalServices', 'Expense_GlobalTaxExpense',
  'Expense_IncomeTaxExpense', 'Expense_Insurance', 'Expense_InterestPaid',
  'Expense_LegalProfessionalFees', 'Expense_OfficeGeneralAdministrativeExpenses',
  'Expense_OtherCurrentOperatingCharges', 'Expense_OtherMiscellaneousServiceCost',
  'Expense_PayrollExpenses', 'Expense_RentOrLeaseOfBuildings', 'Expense_RepairMaintenance',
  'Expense_ShippingFreightDelivery', 'Expense_StaffCosts', 'Expense_Sundry',
  'Expense_SuppliesMaterials', 'Expense_Travel', 'Expense_TravelExpensesGeneralAndAdminExpenses',
  'Expense_UnappliedCashBillPaymentExpense', 'Expense_Utilities', 'Other Expense_Depreciation',
  'Other Expense_OtherMiscellaneousExpense', 'Other Expense_TaxRoundoffGainOrLoss',
  'Accounts Payable_AccountsPayable', 'Credit Card_CreditCard',
  'Long Term Liability_AccruedVacationPayable', 'Long Term Liability_LongTermBorrowings',
  'Long Term Liability_OtherLongTermLiabilities', 'Other Current Liability_AccruedLiabilities',
  'Other Current Liability_CurrentLiabilities', 'Other Current Liability_GlobalTaxPayable',
  'Other Current Liability_GlobalTaxSuspense', 'Other Current Liability_OtherCurrentLiabilities',
  'Other Current Liability_PayrollClearing', 'Other Current Liability_PayrollTaxPayable',
  'Income_DiscountsRefundsGiven', 'Income_OtherCurrentOperatingIncome', 'Income_OtherPrimaryIncome',
  'Income_SalesOfProductIncome', 'Income_ServiceFeeIncome', 'Income_UnappliedCashPaymentIncome',
  'Other Income_DividendIncome', 'Other Income_InterestEarned', 'Other Income_OtherMiscellaneousIncome',
  'Other Income_OtherOperatingIncome',
]

// Canonical P&L Group order — the summary/subtotal lines of a P&L statement, in standard
// presentation order. The "Summary" Profitability subsection is keyed by "P&L Group";
// the "Detailed" one adds Account Type within each group. Groups not listed sort to the end.
export const PL_GROUP_ORDER: string[] = [
  'Revenue',
  'Cost of Sales',
  'Gross Profit',
  'Operating Expenses',
  'Other Income',
  'Non-Operating Income',
  'Net Profit',
]

// First occurrence wins so the near-duplicate trailing-dot entries keep their earlier rank.
const accountTypeRank = buildRankMap(ACCOUNT_TYPE_ORDER)
const plGroupRank = buildRankMap(PL_GROUP_ORDER)

function buildRankMap(order: string[]): Map<string, number> {
  const map = new Map<string, number>()
  order.forEach((value, index) => {
    const key = value.trim().toUpperCase()
    if (!map.has(key)) map.set(key, index)
  })
  return map
}

function findKeyByName(rows: Array<Record<string, unknown>>, normalized: string): string | null {
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (normalize(key) === normalized) return key
    }
  }
  return null
}

/** The key in a subsection's rows that holds the account-type dimension, or null. */
export function findAccountTypeKey(rows: Array<Record<string, unknown>>): string | null {
  return findKeyByName(rows, 'accounttype')
}

/** The key in a subsection's rows that holds the P&L-group dimension, or null. */
export function findPlGroupKey(rows: Array<Record<string, unknown>>): string | null {
  return findKeyByName(rows, 'plgroup')
}

/** Rank of a value in a canonical order map; unlisted/non-string → +Infinity. */
function rankOf(rankMap: Map<string, number>, value: unknown): number {
  if (typeof value !== 'string') return Number.POSITIVE_INFINITY
  const rank = rankMap.get(value.trim().toUpperCase())
  return rank === undefined ? Number.POSITIVE_INFINITY : rank
}

/**
 * Reorder Profitability rows into the canonical accounting sequence: by P&L Group first,
 * then by Account Type within each group. Applies whichever of those columns are present
 * (Summary has P&L Group; Detailed adds Account Type); returns rows unchanged if neither
 * is found. Stable — rows sharing a rank (including the unlisted tail) keep their original
 * relative order. Returns a new array, never mutating the input.
 */
export function sortProfitabilityRows(
  rows: Array<Record<string, unknown>>
): Array<Record<string, unknown>> {
  const plKey = findPlGroupKey(rows)
  const atKey = findAccountTypeKey(rows)
  if (!plKey && !atKey) return rows
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      if (plKey) {
        const diff = rankOf(plGroupRank, a.row[plKey]) - rankOf(plGroupRank, b.row[plKey])
        if (diff !== 0) return diff
      }
      if (atKey) {
        const diff = rankOf(accountTypeRank, a.row[atKey]) - rankOf(accountTypeRank, b.row[atKey])
        if (diff !== 0) return diff
      }
      return a.index - b.index
    })
    .map((entry) => entry.row)
}

/** Format a month dimension value ("2025-10-01") as a short label ("Oct 2025"). */
export function formatMonthValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  const base = dayjs(String(value).slice(0, 10))
  return base.isValid() ? base.format('MMM YYYY') : String(value)
}

/** Chronological order for month dimension values; falls back to string order when unparseable. */
export function compareMonthValues(a: string, b: string): number {
  const da = dayjs(a.slice(0, 10))
  const db = dayjs(b.slice(0, 10))
  if (da.isValid() && db.isValid()) return da.valueOf() - db.valueOf()
  return a.localeCompare(b)
}

/** Returns the subsection's rows from a report's JSON (matched ignoring case/punctuation), or null if absent. */
export function lookupSubsection(
  reportJson: AdvisoryAiReportRawData | null | undefined,
  subsection: string
): Array<Record<string, unknown>> | null {
  if (!reportJson) return null
  const target = normalize(subsection)
  for (const [key, rows] of Object.entries(reportJson)) {
    if (normalize(key) === target) return Array.isArray(rows) ? rows : []
  }
  return null
}
